import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CDropdown,
  CDropdownDivider,
  CDropdownItem,
  CDropdownMenu,
  CDropdownToggle,
  CFormSwitch,
  CSpinner,
} from "@coreui/react";
import { ColoredValue } from "@src/components/ColoredValue";
import { ExecutionCommandsDropdown } from "@src/components/RunDetails/ExecutionCommandsDropdown";
import { ExecutionOperatorActions } from "@src/components/RunDetails/ExecutionOperatorActions";
import { ExecutionStatusBadge } from "@src/components/RunDetails/ExecutionStatusBadge";
import { RunStatsTable } from "@src/components/RunDetails/RunStatsTable";
import { TagsEditor } from "@src/components/TagsEditor/TagsEditor";
import { useRunDetail } from "@src/contexts/RunDetailContext";
import { useRunCloneActions } from "@src/hooks/useRunCloneActions";
import { useJsonModal } from "@src/hooks/useJsonModal";
import { useToastActions } from "@src/hooks/useToasts";
import { useValueWhileMounted } from "@src/hooks/useValueWhileMounted";
import { tdApi } from "@src/services/api/tdApi";
import {
  GroupMembershipChangeAbandonedError,
  runGroupMembershipChange,
} from "@src/services/api/groupMembership";
import type * as T from "@src/types";
import type { ApiCapitalEvent, ApiExecutionGroup } from "@tradingdroid/frontends-types";
import { formatDateTimeShort } from "@src/utils/dateUtils";
import { hasLiveWorker } from "@src/utils/executionStatus";
import { hasOnlyTrackingEndedPositions } from "@src/utils/positionStatus";

interface Props {
  run: T.ApiRun | null;
  type: "backtest" | "execution";
  headerAction?: React.ReactElement;
}

type ShowDangerToast = (toast: { message: string; type: "danger" }) => void;

export const assignExecutionToGroup = (
  executionId: number,
  groupId: number,
  onWaitingForGroupCache?: () => void,
  isStillWanted?: () => boolean,
) =>
  runGroupMembershipChange(
    () => tdApi.patchExecution(executionId, { execution_group_id: groupId }),
    [groupId],
    onWaitingForGroupCache,
    isStillWanted,
  );

export const createAndAssignExecutionGroup = async (
  executionId: number,
  name: string,
  showToast: ShowDangerToast,
  onWaitingForGroupCache?: () => void,
  isStillWanted?: () => boolean,
): Promise<ApiExecutionGroup> => {
  const newGroup = await tdApi.createExecutionGroup({ name });
  if (!newGroup) throw new Error("Failed to create group");

  try {
    await assignExecutionToGroup(executionId, newGroup.id, onWaitingForGroupCache, isStillWanted);
  } catch (assignErr) {
    // Rollback: delete the orphan group if assignment failed. If that delete also fails there is
    // nothing left to repair from here -- the group is empty by construction -- so name it to the
    // user instead of swallowing it.
    try {
      await tdApi.deleteExecutionGroup(newGroup.id);
    } catch {
      showToast({
        message:
          assignErr instanceof GroupMembershipChangeAbandonedError
            ? `Group "${newGroup.name}" was left empty and could not be deleted.`
            : `Failed to delete group "${newGroup.name}" after assignment failed. The empty group still exists.`,
        type: "danger",
      });
    }
    throw assignErr;
  }

  return newGroup;
};

export const removeExecutionFromGroup = async (
  executionId: number,
  groupId: number,
  onWaitingForGroupCache?: () => void,
  isStillWanted?: () => boolean,
): Promise<void> => {
  // Invalidate group cache so metrics recalculate (best-effort): a refused invalidation leaves the
  // cached metrics describing the old membership, and it must not stop the detach below.
  await tdApi.patchExecutionGroup(groupId, { cache_url: null, cache_ts: null }).catch(() => {});
  await runGroupMembershipChange(
    () => tdApi.patchExecution(executionId, { execution_group_id: null }),
    [groupId],
    onWaitingForGroupCache,
    isStillWanted,
  );
};

export const RunInfoCard: React.FC<Props> = ({ run, type, headerAction }) => {
  const navigate = useNavigate();
  const { showToast } = useToastActions();
  const { showJsonModal } = useJsonModal();
  const { updateRun } = useRunDetail();
  const [isTogglingAddToPortfolio, setIsTogglingAddToPortfolio] = useState(false);
  const [capitalEventsState, setCapitalEventsState] = useState<{
    executionId: number | null;
    items: ApiCapitalEvent[];
  }>({ executionId: null, items: [] });
  const { handleCreateExecution, handleDuplicateBacktest, handleDuplicateExecution } =
    useRunCloneActions(run, type);
  const [compatibleGroups, setCompatibleGroups] = useState<ApiExecutionGroup[] | null>(null);
  const [groupingState, setGroupingState] = useState<"idle" | "busy" | "waiting">("idle");
  const activeExecutionId = useValueWhileMounted(run?.id);
  const latestRun = useRef(run);
  useLayoutEffect(() => {
    latestRun.current = run;
  }, [run]);

  const publishRunFields = useCallback(
    (
      ownFields: Partial<
        Pick<
          T.TDT.ApiExecution,
          "tags" | "add_to_portfolio" | "execution_group_id" | "execution_group"
        >
      >,
    ) => {
      const next = { ...latestRun.current, ...ownFields } as T.ApiRun;
      latestRun.current = next;
      updateRun(next);
    },
    [updateRun],
  );

  // Reset cached groups when navigating between executions
  useEffect(() => {
    setCompatibleGroups(null);
  }, [run?.id]);

  // Only render events that belong to the current execution
  const capitalEvents = capitalEventsState.executionId === run?.id ? capitalEventsState.items : [];

  // Fetch capital movement ledger entries for executions
  // Depends on `run` object reference so it refetches after updateRun() calls
  // For group synthetic runs, fetch events from ALL member executions
  useEffect(() => {
    if (!run || type !== "execution") return;
    let cancelled = false;

    const isGroup = "_is_group_synthetic" in run && !!(run as any)._is_group_synthetic;

    if (isGroup) {
      // Use explicit execution IDs set by ExecutionGroupDetailPage
      const executions: number[] = (run as any)._group_execution_ids || [];

      if (executions.length === 0) {
        setCapitalEventsState({ executionId: run.id, items: [] });
        return;
      }

      Promise.all(
        executions.map((eid) =>
          tdApi
            .getExecutionCapitalEvents(eid, { limit: 100, sort_by: "event_ts:ASC" })
            .then((r) => (r?.items || []).map((ev) => ({ ...ev, execution_id: eid })))
            .catch(() => []),
        ),
      ).then((results) => {
        if (cancelled) return;
        const merged = results
          .flat()
          .sort((a, b) => new Date(b.event_ts).getTime() - new Date(a.event_ts).getTime());
        setCapitalEventsState({ executionId: run.id, items: merged });
      });
    } else {
      tdApi
        .getExecutionCapitalEvents(run.id, { limit: 100, sort_by: "event_ts:DESC" })
        .then((response) => {
          if (!cancelled && response?.items) {
            setCapitalEventsState({ executionId: run.id, items: response.items });
          }
        })
        .catch((err) => {
          if (!cancelled) {
            console.error("[RunInfoCard] Failed to fetch capital events:", err);
          }
        });
    }

    return () => {
      cancelled = true;
    };
  }, [run, type]);

  const handleTagsUpdated = useCallback(
    (newTags: string[]) => {
      if (!run) return;
      if (activeExecutionId.current === run.id) {
        publishRunFields({ tags: newTags });
      }
      showToast({ message: "Tags saved", type: "success" });
    },
    [run, publishRunFields, showToast, activeExecutionId],
  );

  // Refetch the execution after an operator action (resume/stop) so the card reflects the new
  // status/desired_state. Merges over the current run to preserve already-loaded embeds (positions).
  //
  // Reports whether a fresh row was actually installed. Callers that re-enable a control after a
  // refusal depend on that answer: a refusal leaves the screen showing the row the server declined to
  // act on, so handing the control back before the row is replaced invites a second attempt that
  // reads the execution as it has since become rather than the one the operator was looking at.
  const refetchExecution = useCallback(async (): Promise<boolean> => {
    if (!run || type !== "execution") return false;
    try {
      const fresh = await tdApi.getExecution(run.id);
      if (!fresh) return false;
      updateRun({ ...run, ...fresh } as T.TDT.ApiExecution);
      return true;
    } catch (err) {
      console.error("[RunInfoCard] Failed to refresh execution after operator action:", err);
      return false;
    }
  }, [run, type, updateRun]);

  const handleAddToPortfolioToggle = async () => {
    if (!run || type !== "execution") return;

    const execution = run as T.TDT.ApiExecution;
    const newValue = !execution.add_to_portfolio;
    setIsTogglingAddToPortfolio(true);
    try {
      const updatedExecution = await tdApi.patchExecution(run.id, {
        add_to_portfolio: newValue,
      });
      if (updatedExecution && activeExecutionId.current === run.id) {
        // Merge the portfolio flag into the latest run to preserve its other fields.
        publishRunFields({ add_to_portfolio: updatedExecution.add_to_portfolio });
      }
      showToast({
        message: newValue ? "Added to portfolio" : "Removed from portfolio",
        type: newValue ? "success" : "info",
      });
    } catch (error) {
      console.error("[RunInfoCard] Failed to toggle add_to_portfolio:", error);
      showToast({
        message: "Failed to update portfolio status",
        type: "danger",
      });
    } finally {
      setIsTogglingAddToPortfolio(false);
    }
  };

  const fetchCompatibleGroups = async () => {
    if (compatibleGroups !== null || !run || type !== "execution") return;
    try {
      const result = await tdApi.getExecutionGroups({ embed: "executions", limit: 100 });
      const currentExec = run as T.TDT.ApiExecution;
      const groups = (result?.items || []).filter((g: ApiExecutionGroup) =>
        g.executions?.some(
          (ex) =>
            ex.strategy_id === currentExec.strategy_id &&
            ex.config?.symbol_name === currentExec.config?.symbol_name &&
            ex.config?.exchange_name === currentExec.config?.exchange_name &&
            ex.config?.timeframe === currentExec.config?.timeframe,
        ),
      );
      setCompatibleGroups(groups);
    } catch (err) {
      console.error("[GroupDropdown] Error fetching groups:", err);
      setCompatibleGroups([]);
    }
  };

  const runMembershipAction = async <T,>(
    runId: number,
    change: (onWaiting: () => void, isStillWanted: () => boolean) => Promise<T>,
    patch: (result: T) => void,
    successMessage: string,
    failureMessage: string,
  ) => {
    const onWaiting = () => setGroupingState("waiting");
    const isStillWanted = () => activeExecutionId.current === runId;
    setGroupingState("busy");
    try {
      const result = await change(onWaiting, isStillWanted);
      if (isStillWanted()) {
        patch(result);
        showToast({ message: successMessage, type: "success" });
      }
    } catch (err) {
      if (err instanceof GroupMembershipChangeAbandonedError) return;
      const msg = err instanceof Error ? err.message : String(err);
      showToast({ message: `${failureMessage}: ${msg}`, type: "danger" });
    } finally {
      setGroupingState("idle");
    }
  };

  const handleAssignToGroup = async (groupId: number) => {
    if (!run) return;
    await runMembershipAction(
      run.id,
      (onWaiting, isStillWanted) =>
        assignExecutionToGroup(run.id, groupId, onWaiting, isStillWanted),
      () => {
        const assignedGroup = compatibleGroups?.find((g) => g.id === groupId);
        publishRunFields({ execution_group_id: groupId, execution_group: assignedGroup });
      },
      "Added to group",
      "Failed to add to group",
    );
  };

  const handleCreateNewGroup = async () => {
    if (!run) return;
    const symbol = run.config?.symbol_name || "Unknown";
    const strategy = run.config?.strategy_name || "Strategy";
    const n = (compatibleGroups?.length || 0) + 1;
    const name = `${symbol} ${strategy} Group ${n}`;
    await runMembershipAction(
      run.id,
      (onWaiting, isStillWanted) =>
        createAndAssignExecutionGroup(run.id, name, showToast, onWaiting, isStillWanted),
      (newGroup) => {
        publishRunFields({ execution_group_id: newGroup.id, execution_group: newGroup });
        // Reset cached groups so next open refetches
        setCompatibleGroups(null);
      },
      `Created group "${name}"`,
      "Failed to assign execution to group",
    );
  };

  const handleRemoveFromGroup = async () => {
    if (!run) return;
    const execution = run as T.TDT.ApiExecution;
    const groupId = execution.execution_group_id;
    if (!groupId) return;
    await runMembershipAction(
      run.id,
      (onWaiting, isStillWanted) =>
        removeExecutionFromGroup(run.id, groupId, onWaiting, isStillWanted),
      () => {
        publishRunFields({ execution_group_id: null, execution_group: undefined });
        // Reset cached groups so next open refetches
        setCompatibleGroups(null);
      },
      "Removed from group",
      "Failed to remove from group",
    );
  };

  if (!run) {
    return (
      <CCard className="mb-3">
        <CCardBody className="text-center p-4">
          <CSpinner />
          <p className="mt-2">Loading {type} details...</p>
        </CCardBody>
      </CCard>
    );
  }

  const metrics = ("live_metrics" in run ? run.live_metrics : undefined) ?? run.metrics;
  const onlyTrackingEnded = hasOnlyTrackingEndedPositions(run.positions);
  const unknownReturn = <span className="RunInfoUnknown text-muted">Unknown</span>;

  return (
    <CCard className="RunDetailPageSummary mb-3">
      <CCardHeader className="d-flex flex-column flex-lg-row align-items-start align-items-lg-center gap-2">
        <h5 className="mb-0">
          #{run.id} - {run.name}
          <CButton
            color="info"
            variant="ghost"
            size="sm"
            className="ms-2"
            onClick={() => {
              const { positions: _positions, ...runWithoutPositions } = run;
              showJsonModal(runWithoutPositions, `Full ${type}: "${run.name}"`);
            }}
            title="View configuration"
          >
            ℹ️
          </CButton>
        </h5>
        {headerAction && <div className="ms-lg-auto">{headerAction}</div>}
        {!headerAction && type === "backtest" && (
          <div className="ms-lg-auto d-flex gap-2">
            <CButton color="secondary" size="sm" onClick={handleDuplicateBacktest}>
              Duplicate
            </CButton>
            <CButton color="success" size="sm" onClick={handleCreateExecution}>
              Create Execution
            </CButton>
          </div>
        )}
        {!headerAction && type === "execution" && (
          <div className="d-flex flex-wrap align-items-center gap-2 ms-lg-auto">
            <CDropdown
              className="GroupDropdown"
              onShow={
                !(run as T.TDT.ApiExecution).execution_group_id ? fetchCompatibleGroups : undefined
              }
            >
              <CDropdownToggle
                color="info"
                variant="outline"
                size="sm"
                disabled={groupingState !== "idle"}
              >
                {groupingState !== "idle" ? (
                  <>
                    <CSpinner size="sm" />
                    {groupingState === "waiting" && (
                      <span className="groupCacheWaitingLabel ms-1">Waiting for group cache…</span>
                    )}
                  </>
                ) : (run as T.TDT.ApiExecution).execution_group_id ? (
                  `Group #${(run as T.TDT.ApiExecution).execution_group_id}`
                ) : (
                  "Group"
                )}
              </CDropdownToggle>
              <CDropdownMenu>
                {(run as T.TDT.ApiExecution).execution_group_id ? (
                  <>
                    <CDropdownItem
                      onClick={() =>
                        navigate(`/exec-group/${(run as T.TDT.ApiExecution).execution_group_id}`)
                      }
                    >
                      View Group
                    </CDropdownItem>
                    <CDropdownDivider />
                    <CDropdownItem className="text-danger" onClick={handleRemoveFromGroup}>
                      Remove from Group
                    </CDropdownItem>
                  </>
                ) : compatibleGroups === null ? (
                  <CDropdownItem disabled className="text-center">
                    <CSpinner size="sm" />
                  </CDropdownItem>
                ) : (
                  <>
                    {compatibleGroups.map((g) => (
                      <CDropdownItem key={g.id} onClick={() => handleAssignToGroup(g.id)}>
                        Add to Group #{g.id}
                      </CDropdownItem>
                    ))}
                    {compatibleGroups.length > 0 && <CDropdownDivider />}
                    <CDropdownItem onClick={handleCreateNewGroup}>+ Create new group</CDropdownItem>
                  </>
                )}
              </CDropdownMenu>
            </CDropdown>
            <CButton color="secondary" size="sm" onClick={handleDuplicateExecution}>
              Duplicate
            </CButton>
            {run.config && hasLiveWorker(run) && (
              <ExecutionCommandsDropdown
                executionId={run.id}
                exchangeName={run.config.exchange_name}
                symbolName={run.config.symbol_name}
                onExecutionChanged={refetchExecution}
              />
            )}
            {!run.end_ts && !hasLiveWorker(run) && (
              <ExecutionOperatorActions
                execution={run as T.TDT.ApiExecution}
                onActionComplete={refetchExecution}
              />
            )}
            <CFormSwitch
              id={`addToPortfolioSwitch-${run.id}`}
              label="Add to Portfolio"
              checked={!!(run as T.TDT.ApiExecution).add_to_portfolio}
              disabled={isTogglingAddToPortfolio}
              onChange={handleAddToPortfolioToggle}
            />
          </div>
        )}
      </CCardHeader>
      <CCardBody>
        <div className="RunDetailSummary">
          <div className="summaryColumn summaryColumn-info">
            <div className="SummaryItem info-startDate">
              <strong>Start Date:</strong>
              <span>{run.start_ts ? formatDateTimeShort(run.start_ts) : "-"}</span>
            </div>
            <div className="SummaryItem info-endDate">
              <strong>End Date:</strong>
              <span>
                {run.end_ts ? (
                  formatDateTimeShort(run.end_ts)
                ) : type === "execution" ? (
                  <span className="RunInfoCard-liveStatus d-inline-flex align-items-center gap-1 flex-wrap">
                    <ExecutionStatusBadge
                      statusName={(run as T.TDT.ApiExecution).status_name}
                      suspendCauseName={(run as T.TDT.ApiExecution).suspend_cause_name}
                      recoveryNotBeforeTs={(run as T.TDT.ApiExecution).recovery_not_before_ts}
                    />
                    {run.start_ts && (
                      <span className="RunInfoCard-liveDuration text-muted">
                        {Math.floor((Date.now() - new Date(run.start_ts).getTime()) / 86400000)}d
                      </span>
                    )}
                  </span>
                ) : run.start_ts ? (
                  <span className="text-success">
                    Running |{" "}
                    {Math.floor((Date.now() - new Date(run.start_ts).getTime()) / 86400000)}d
                  </span>
                ) : (
                  "-"
                )}
              </span>
            </div>
            <div className="SummaryItem info-initialCapital">
              <strong>Initial Capital:</strong>
              <span>
                <ColoredValue
                  value={run.config?.portfolio?.initial_capital ?? 0}
                  type="currency"
                  decimals={2}
                />
              </span>
            </div>
            <div className="SummaryItem info-investedCapital">
              <strong>
                Invested Capital:
                {capitalEvents.length > 0 && (
                  <CButton
                    color="info"
                    variant="ghost"
                    size="sm"
                    className="capitalMovementsInfo ms-1 p-0"
                    onClick={() =>
                      showJsonModal(
                        capitalEvents.map((event) => ({
                          id: event.id,
                          ...("execution_id" in event ? { execution_id: event.execution_id } : {}),
                          type: event.event_type,
                          amount: event.amount,
                          reason: event.reason,
                          date: event.event_ts,
                          strategy_capital_at_event: event.strategy_capital_at_event,
                          available_capital_before: event.available_capital_before,
                          available_capital_after: event.available_capital_after,
                          execution_command_id: event.execution_command_id,
                          source_position_id: event.source_position_id,
                        })),
                        `Capital Movements (${capitalEvents.length})`,
                      )
                    }
                    title="View capital movements"
                  >
                    ℹ️
                  </CButton>
                )}
              </strong>
              <span>
                <ColoredValue
                  value={metrics?.invested_capital ?? run.config?.portfolio?.initial_capital ?? 0}
                  type="currency"
                  decimals={2}
                />
              </span>
            </div>
            <div className="SummaryItem info-profit">
              <strong>Profit:</strong>
              <span>
                {onlyTrackingEnded ? (
                  unknownReturn
                ) : (
                  <ColoredValue
                    value={metrics?.cumulative_returns ?? 0}
                    threshold={0}
                    type="currency"
                    decimals={3}
                  />
                )}
              </span>
            </div>
          </div>
          <div className="summaryColumn summaryColumn-stats">
            <RunStatsTable metrics={metrics} onlyTrackingEnded={onlyTrackingEnded} />
          </div>
          <div className="summaryColumn summaryColumn-returns">
            <div className="SummaryItem info-pnl">
              <strong>PnL%:</strong>
              <span>
                {onlyTrackingEnded ? (
                  unknownReturn
                ) : metrics?.cash_on_cash_return_pct != null ? (
                  <ColoredValue
                    value={metrics.cash_on_cash_return_pct}
                    threshold={0}
                    type="percentage"
                    decimals={2}
                  />
                ) : metrics?.cumulative_returns_pct != null ? (
                  <ColoredValue
                    value={metrics.cumulative_returns_pct}
                    threshold={0}
                    type="percentage"
                    decimals={2}
                  />
                ) : (
                  "-"
                )}
              </span>
            </div>
            <div className="SummaryItem info-apy">
              <strong>APY%:</strong>
              <span>
                {onlyTrackingEnded ? (
                  unknownReturn
                ) : (
                  <ColoredValue
                    value={metrics?.annualized_return_pct ?? 0}
                    threshold={0}
                    type="percentage"
                    decimals={2}
                  />
                )}
              </span>
            </div>
            {metrics?.cash_on_cash_return_pct != null && (
              <>
                <div className="SummaryItem info-twr">
                  <strong>TWR%:</strong>
                  <span>
                    {onlyTrackingEnded ? (
                      unknownReturn
                    ) : (
                      <ColoredValue
                        value={metrics.cumulative_returns_pct}
                        threshold={0}
                        type="percentage"
                        decimals={2}
                      />
                    )}
                  </span>
                </div>
                <div className="SummaryItem info-cashOnCash">
                  <strong>Cash-on-Cash%:</strong>
                  <span>
                    {onlyTrackingEnded ? (
                      unknownReturn
                    ) : (
                      <ColoredValue
                        value={metrics.cash_on_cash_return_pct}
                        threshold={0}
                        type="percentage"
                        decimals={2}
                      />
                    )}
                  </span>
                </div>
              </>
            )}
            {"_is_group_synthetic" in run ? (
              run.tags &&
              run.tags.length > 0 && (
                <div className="SummaryItem info-tags">
                  <strong>Tags:</strong>
                  <span>{run.tags.join(", ")}</span>
                </div>
              )
            ) : (
              <div className="SummaryItem info-tags">
                <strong>Tags:</strong>
                <span>
                  <TagsEditor
                    tags={run.tags ?? []}
                    entityType={type}
                    entityId={run.id}
                    onTagsUpdated={handleTagsUpdated}
                    onError={(msg) => showToast({ message: msg, type: "danger" })}
                  />
                </span>
              </div>
            )}
          </div>
        </div>
      </CCardBody>
    </CCard>
  );
};
